import dayjs from 'dayjs'
import { TrashIcon } from '@radix-ui/react-icons'
import useSWRMutation from 'swr/mutation'
import { delFetcher, mutateRequest } from '@/hooks'
import { Loader2 } from 'lucide-react'
import MyLink from '@/components/zthd/my-link'
import { CustomBadge } from '@/components/zthd/badge'
import { getStatusConfig } from '@/comm/campaign-status'
import {
  getEventOnlyCountryDisplay,
  getNoteReasonLabel,
  isEventOnlyCampaignDayRow,
  isEventOnlyMultiScope,
} from '@/features/roas/notes/platform-changes'
export const IndexColumn = {
  id: 'index',
  header: '#',
  cell: ({ row }) => row.index + 1,
}

export const DateColumn = {
  accessorKey: 'date',
  header: 'Date',
  cell: ({ row }) => row.getValue('date'),
}

export const ReasonColumn = {
  accessorKey: 'reason',
  header: 'Note',
  cell: ({ row }) => {
    const reason = row.getValue('reason')
    return reason || (isEventOnlyCampaignDayRow(row.original)
      ? getNoteReasonLabel(row.original)
      : reason)
  },
}

export const CreatedAtColumn = {
  accessorKey: 'createdAt',
  header: 'Logtime',
  cell: ({ row }) => {
    const updateTime = row.getValue('createdAt')
    const formatted = dayjs(updateTime).format('YYYY-MM-DD LT')
    return formatted
  },
}
export const OptColumn = {
  id: 'opt',
  accessorFn: (row) => row.id,
  header: 'Opt',
  cell: ({ row }) => <ActionCell row={row} />,
}

export const CampaignNameColumn = {
  id: 'campaignName',
  header: 'Campaign Name',
  cell: ({ row }) => {
    const note = row.original
    return (
      <div className="flex items-center gap-2">
        <MyLink to={`/troas/campaigns/${note.campaignId}`} target="_blank">
          <span>{note.campaignName}</span>
        </MyLink>
      </div>
    )
  },
}

export const CampaignStateColumn = {
  id: 'campaignState',
  header: 'Campaign State',
  cell: ({ row }) => {
    const note = row.original
    if (isEventOnlyMultiScope(note)) {
      return (
        <CustomBadge variant="default" size="sm" title="多状态合投 Campaign">
          Multiple
        </CustomBadge>
      )
    }

    const status = getStatusConfig(note.campaignState)

    return (
      <CustomBadge variant={status.variant} size="sm">
        {status.label}
      </CustomBadge>
    )
  },
}

export const MediaSourceColumn = {
  id: 'mediaSource',
  header: 'Media Source',
  cell: ({ row }) => {
    const note = row.original
    return <span>{note.mediaSource}</span>
  },
}

export const CountryColumn = {
  id: 'country',
  header: 'Country',
  cell: ({ row }) => {
    const note = row.original
    const country = getEventOnlyCountryDisplay(note)
    return <span title={country.title}>{country.label}</span>
  },
}
export const columns = [
  IndexColumn,
  DateColumn,
  ReasonColumn,
  CreatedAtColumn,
  OptColumn,
]

const ActionCell = ({ row }) => {
  const note = row.original
  const id = note.noteId ?? note.id
  const campaignId = note.campaignId
  const { trigger, isMutating } = useSWRMutation(
    isEventOnlyCampaignDayRow(note) ? null : `/campaigns/${campaignId}/notes/`,
    delFetcher,
  )

  const handleDelete = async () => {
    if (isEventOnlyCampaignDayRow(note) || isMutating) {
      return
    }
    await trigger(id)
    mutateRequest(`/campaigns/${campaignId}/notes`)
    mutateRequest('/troas/notes')
  }

  if (isEventOnlyCampaignDayRow(note)) {
    return null
  }

  if (isMutating) {
    return <Loader2 className="h-4 w-4 animate-spin" />
  }

  return (
    <TrashIcon
      className={`h-4 w-4 text-destructive hover:outline`}
      data-clickable-cell="true"
      onClick={handleDelete}
    />
  )
}
